'use client';

import { useEffect, useMemo, useState } from 'react';
import {
  useClearAssetSelection,
  useGetSelectedAssets,
} from '@frameio/selection';
import {
  useActiveAssetId,
  useActiveCollectionId,
  useActiveFolderId,
  useActiveVersionId,
} from '@frameio/shared/contexts';
import { useCurrentPageType } from '@frameio/shared/routing';
import { useDeactivateAsset } from '../../../../shared';

/**
 * Hook that manages asset selection state and synchronizes it with active
 * assets. Handles clearing selection on container/folder changes and route
 * changes and deactivates active assets when they are no longer in the
 * selection set.
 */
export function FinderAssetSelection() {
  let activeAssetId = useActiveAssetId();
  const activeCollectionId = useActiveCollectionId();
  const activeFolderId = useActiveFolderId();
  const containerId = activeFolderId ?? activeCollectionId;
  const activeVersionId = useActiveVersionId();
  const [prevContainerId, setPrevContainerId] = useState(containerId);
  const deactivateAsset = useDeactivateAsset();
  const currentPageType = useCurrentPageType();
  const [prevPageType, setPrevPageType] = useState(currentPageType);
  const shouldHandleSelectionChange =
    currentPageType === 'collection' ||
    currentPageType === 'shareBuilder' ||
    currentPageType === 'shareReviewFinder' ||
    currentPageType === 'trash';

  if (activeVersionId) {
    activeAssetId = activeVersionId;
  }

  const clearSelection = useClearAssetSelection();
  const selectionSet = useGetSelectedAssets();
  const selection = useMemo(() => new Set(selectionSet), [selectionSet]);

  // this effect clears the selection on folder change
  useEffect(() => {
    if (containerId !== prevContainerId) {
      setPrevContainerId(containerId);
      clearSelection();
    }
  }, [containerId, prevContainerId, clearSelection]);

  // this effect clears the selection on route change
  useEffect(() => {
    if (currentPageType !== prevPageType) {
      setPrevPageType(currentPageType);
      clearSelection();
    }
  }, [currentPageType, prevPageType, clearSelection]);

  /**
   * Any time the selection changes, we can check if the activeAssetId is no longer
   * in the selection and deactivate it if it's not. Restricting this to
   * collections views and project views when the project flag is on for now as
   * those are the only places where Finder 2 selection:is currently being used.
   */
  useEffect(() => {
    if (
      shouldHandleSelectionChange &&
      activeCollectionId &&
      activeAssetId &&
      !selection.has(activeAssetId)
    ) {
      deactivateAsset(activeAssetId, selection);
    }
  }, [
    activeAssetId,
    activeCollectionId,
    selection,
    shouldHandleSelectionChange,
    deactivateAsset,
  ]);

  return null;
}
